import { PaginationQuery } from '../../core/utils/paginationTransformer';
import adminRequest from '../../../config/adminRequest';
import { UsdtUri } from '../../core/configs/services';
import { AdminListResponse } from '../models/adminListResponse';
import { BoSignHelper } from '../../../core/providers/helper/boSign-helper';

export type SweepStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

export type AbandonWallet = {
  walletAddress: string;
  channel: string;
  token: string;
  systemId: string;
  available: boolean;
  txnBalance: number | string;
  availableBalance: number | string;
  /** 鏈上真實餘額。null = 還沒掃過,或掃了但查不到 —— 跟「餘額 0」是兩回事。 */
  chainBalance: number | string | null;
  chainScannedAt: string | null;
  sweepStatus: SweepStatus | null;
  sweepTxId: string | null;
  sweepError: string | null;
  sweepAt: string | null;
};

export type AbandonPreview = {
  systemId: string;
  status: string;
  mchWallets: number;
  userWallets: number;
  /** 非 TRON 而被排除的列數 —— 讓操作者知道「不是全部」。 */
  skippedOtherChannel: number;
};

export type AbandonWalletChangeLog = {
  txnId: string;
  txnHash: string;
  walletAddress: string;
  systemId: string;
  orderNo: string;
  channel: string;
  token: string;
  availableAmount: number;
  txnAmount: number;
  availableBeforeBalance: number;
  availableAfterBalance: number;
  txnBeforeBalance: number;
  txnAfterBalance: number;
  comment: string;
  createdBy: string;
};

export type TransferReq = {
  token: string;
  channel: string;
  destAddress: string;
  orderAmount: number;
  walletAddress: string;
  operatorId: string;
  systemId: string;
  code: string;
};

export type AbandonWalletTransferOrder = {
  orderId: string;
  orderNo: string;
  systemId: string;
  status: string;
  transactionHash: string;
  transferTaskId: string;
  walletAddress: string;
  toAddress: string;
  orderAmount: number;
  channel: string;
  token: string;
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  updatedBy: string;
};

export class AbandonWalletProvider {
  static async getList(param: PaginationQuery) {
    const res = await adminRequest.get<AdminListResponse<AbandonWallet>>(
      UsdtUri('admin/abandon/wallet'),
      { params: param },
    );
    return res.data;
  }

  static async getChangeLogList(param: PaginationQuery) {
    const res = await adminRequest.get<
      AdminListResponse<AbandonWalletChangeLog>
    >(UsdtUri('admin/abandon/wallet-txn'), { params: param });
    return res.data;
  }

  static async getTransferOrderList(param: PaginationQuery) {
    const res = await adminRequest.get<
      AdminListResponse<AbandonWalletTransferOrder>
    >(UsdtUri('admin/abandon/order'), { params: param });
    return res.data;
  }

  /** 預覽整批棄用會影響幾顆。不寫入。 */
  static async previewAbandonMerchant(systemId: string) {
    const res = await adminRequest.get<AbandonPreview>(
      UsdtUri('admin/abandon/wallet/preview'),
      { params: { systemId } },
    );
    return res.data;
  }

  /** 把停用/禁用商戶底下的商戶錢包 + 用戶錢包一次全部標為棄用。 */
  static async abandonMerchant(data: { systemId: string; code: string }) {
    const res = await adminRequest.post(
      UsdtUri('admin/abandon/wallet/abandon-merchant'),
      { ...data, boSign: BoSignHelper.generate(data) },
    );
    return res.data;
  }

  /**
   * 單獨棄用指定的幾顆錢包 —— 不必連坐整個商戶。
   *
   * 後端會逐顆確認:必須是我們有私鑰的商戶/用戶錢包,而且不能是運營錢包。
   * 回傳 notFound 讓操作者知道哪幾顆沒被加進去。
   */
  static async abandonWallets(data: { addresses: string[]; code: string }) {
    const res = await adminRequest.post<{ inserted: number; notFound: string[] }>(
      UsdtUri('admin/abandon/wallet/abandon-wallets'),
      { ...data, boSign: BoSignHelper.generate(data) },
    );
    return res.data;
  }

  /** 排進鏈上餘額掃描佇列。只讀鏈,所以不用 2FA。 */
  static async scanChain(addresses: string[]) {
    const res = await adminRequest.post(
      UsdtUri('admin/abandon/wallet/scan-chain'),
      { addresses },
    );
    return res.data;
  }

  /**
   * 排進清空佇列。
   *
   * 刻意沒有目的地參數 —— 目的地由後端從 RETIRE_SWEEP_DEST 讀。這條路徑能掃空任意
   * 一顆我們有私鑰的錢包,目的地要是能從前端指定,一個被盜用的後台帳號就能把錢搬走。
   */
  static async sweepChain(data: { addresses: string[]; code: string }) {
    const res = await adminRequest.post(
      UsdtUri('admin/abandon/wallet/sweep-chain'),
      { ...data, boSign: BoSignHelper.generate(data) },
    );
    return res.data;
  }

  /** 停用指定錢包(available=0)。棄用前的必經步驟,需 2FA + boSign。 */
  static async disableWallets(data: { addresses: string[]; code: string }) {
    const res = await adminRequest.post<{ disabled: number; notFound: string[] }>(
      UsdtUri('admin/abandon/wallet/disable-wallets'),
      { ...data, boSign: BoSignHelper.generate(data) },
    );
    return res.data;
  }

  /** 移出棄用清單。已經清空過的會被後端拒絕。 */
  static async unabandon(data: { addresses: string[]; code: string }) {
    const res = await adminRequest.post(
      UsdtUri('admin/abandon/wallet/unabandon'),
      { ...data, boSign: BoSignHelper.generate(data) },
    );
    return res.data;
  }

  static async transfer(data: TransferReq) {
    const boSign = BoSignHelper.generate(data);
    const res = await adminRequest.post(
      UsdtUri('admin/abandon/wallet/transfer'),
      {
        ...data,
        boSign,
      },
    );
    return res.data;
  }
}
